import { useEffect, useRef, useSyncExternalStore } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { graphql, type ResultOf } from "gql.tada";
import { graphqlClient } from "@/graphql/client";

const IDLE_TIMEOUT_MS = 5 * 60 * 1000; // 5 minutes
const ACTIVITY_EVENTS = ["pointerdown", "keydown", "scroll", "touchstart"] as const;

let active = true;
const listeners = new Set<() => void>();
let timer: ReturnType<typeof setTimeout> | undefined;

function goIdle() {
  if (!active) return;
  active = false;
  listeners.forEach((l) => {
    l();
  });
}

function goActive() {
  clearTimeout(timer);
  timer = setTimeout(goIdle, IDLE_TIMEOUT_MS);
  if (active) return;
  active = true;
  listeners.forEach((l) => {
    l();
  });
}

if (typeof window !== "undefined") {
  for (const event of ACTIVITY_EVENTS) {
    window.addEventListener(event, goActive, { capture: true, passive: true });
  }
  timer = setTimeout(goIdle, IDLE_TIMEOUT_MS);
}

function useUserActive(): boolean {
  const initialized = useRef(false);
  useEffect(() => {
    if (!initialized.current) {
      initialized.current = true;
      goActive(); // reset timer on mount
    }
  }, []);
  return useSyncExternalStore(
    (cb) => {
      listeners.add(cb);
      return () => listeners.delete(cb);
    },
    () => active,
    () => true,
  );
}

// ─── GraphQL queries ────────────────────────────────────────────────────────

const NotificationsQuery = graphql(`
  query Notifications($limit: Int, $offset: Int) {
    notifications(limit: $limit, offset: $offset) {
      id
      type
      title
      message
      relatedId
      read
      createdAt
    }
  }
`);

const UnreadNotificationCountQuery = graphql(`
  query UnreadNotificationCount {
    unreadNotificationCount
  }
`);

// ─── GraphQL mutations ──────────────────────────────────────────────────────

const MarkNotificationReadMutation = graphql(`
  mutation MarkNotificationRead($id: String) {
    markNotificationRead(id: $id) {
      success
      error
    }
  }
`);

// ─── Inferred types ─────────────────────────────────────────────────────────

export type Notification = ResultOf<typeof NotificationsQuery>["notifications"][number];

// ─── Query hooks ─────────────────────────────────────────────────────────────

export function useNotifications(limit?: number, offset?: number) {
  return useQuery({
    queryKey: ["notifications", limit, offset],
    queryFn: () =>
      graphqlClient
        .request(NotificationsQuery, {
          limit,
          offset,
        })
        .then((d) => d.notifications),
  });
}

export function useUnreadCount() {
  const isActive = useUserActive();
  return useQuery({
    queryKey: ["unreadNotificationCount"],
    queryFn: () =>
      graphqlClient.request(UnreadNotificationCountQuery).then((d) => d.unreadNotificationCount),
    refetchInterval: isActive ? 60_000 : false,
  });
}

// ─── Mutation hooks ──────────────────────────────────────────────────────────

export function useMarkNotificationRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id?: string) =>
      graphqlClient
        .request(MarkNotificationReadMutation, { id })
        .then((d) => d.markNotificationRead),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["notifications"] });
      void queryClient.invalidateQueries({ queryKey: ["unreadNotificationCount"] });
    },
  });
}
